import React from 'react';
import { Link } from 'react-router-dom';
import '../css/About.css';
import '../css/Distributors.css';
import SeoHead from './Shared/SeoHead';

const Distributors = () => {
    const siteUrl = window.location.origin;

    const schema = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'MHE Electronics',
        url: `${siteUrl}/distributors`,
        logo: `${siteUrl}/MHELogo.svg`,
        description: 'MHE Electronics is the sole owner, manufacturer and original seller of the MCS and MCS-NX product range. Any other company supplying MCS or MCS-NX products is an authorised distributor of MHE Electronics, not the manufacturer.',
        brand: [
            {
                "@type": "Brand",
                name: "MCS"
            },
            {
                "@type": "Brand",
                name: "MCS-NX"
            }
        ],
        subjectOf: [
            {
                '@type': 'Organization',
                name: 'Standby UK',
                url: 'https://standbygroup.com/uk/',
                description: 'Authorised MHE Electronics distributor for the United Kingdom and Republic of Ireland.',
                areaServed: ['United Kingdom', 'Ireland']
            },
            {
                '@type': 'Organization',
                name: 'Matins d.o.o.',
                url: 'https://matins.hr/?lang=en',
                description: 'Authorised MHE Electronics distributor for Croatia and Central / Eastern Europe.',
                areaServed: ['Croatia', 'Central Europe', 'Eastern Europe']
            }
        ]
    };

    return (
        <div className="about-page">
            <SeoHead
                title="Authorised Distributors | MCS &amp; MCS-NX by MHE Electronics"
                description="MHE Electronics is the sole owner, manufacturer and original seller of the MCS and MCS-NX product range. All other suppliers are authorised distributors of MHE Electronics."
                canonical={`${siteUrl}/distributors`}
                schema={schema}
            />

            {/* ===== Hero ===== */}
            <div className="about-hero">
                <span className="about-hero-eyebrow">Authorised distributors</span>
                <h1 className="about-hero-title">
                    Manufactured by MHE. <span>Supplied worldwide.</span>
                </h1>
                <p className="about-hero-sub">
                    <strong>MHE Electronics is the sole owner, manufacturer and original seller of the MCS and MCS-NX product range.</strong>{' '}
                    Any other company supplying MCS or MCS-NX products does so as an authorised distributor of MHE
                    Electronics &mdash; not as the manufacturer or brand owner.
                </p>
            </div>

            <div className="about-content">
                {/* ===== Manufacturer authority ===== */}
                <section className="about-authority-section">
                    <span className="about-section-eyebrow">The manufacturer</span>
                    <h2 className="about-section-title">MCS &amp; MCS-NX &mdash; owned and manufactured by MHE Electronics</h2>
                    <p className="about-section">
                        MHE Electronics is the <strong>sole owner, original developer, manufacturer and original seller</strong>{' '}
                        of the MCS and MCS-NX product range. The MCS and MCS-NX brands and designs are the intellectual
                        property of MHE Electronics. Any other company supplying these products is an authorised
                        distributor of MHE Electronics &mdash; they are not the manufacturer or brand owner.
                    </p>
                    <div className="about-authority-grid">
                        <div className="about-authority-card">
                            <h3>Sole brand owner</h3>
                            <p>MHE Electronics owns the MHE, MCS and MCS-NX brands and the intellectual property behind every product in the range.</p>
                        </div>
                        <div className="about-authority-card">
                            <h3>Sole manufacturer</h3>
                            <p>The MCS and MCS-NX product range is designed, assembled, programmed and tested exclusively at MHE Electronics facilities.</p>
                        </div>
                        <div className="about-authority-card">
                            <h3>Original seller</h3>
                            <p>MHE Electronics is the original seller of MCS and MCS-NX products. Distributors purchase finished products from MHE and resell them in their territories.</p>
                        </div>
                        <div className="about-authority-card">
                            <h3>Distributors are resellers</h3>
                            <p>Any third party supplying MCS or MCS-NX products is an authorised distributor only &mdash; not the manufacturer, brand owner, or developer.</p>
                        </div>
                    </div>
                </section>

                {/* ===== Distributor network ===== */}
                <section className="distributors-network">
                    <span className="about-section-eyebrow">Distributor network</span>
                    <h2 className="about-section-title">Our authorised distributors</h2>
                    <p className="about-section">
                        MHE products are supplied internationally through the following authorised distribution
                        partners. Each partner is approved to resell genuine MHE-manufactured products in their
                        respective territories.
                    </p>

                    <div className="distributors-grid">
                        <div className="distributor-card">
                            <div className="distributor-flag">🇬🇧</div>
                            <h3>Standby UK</h3>
                            <p className="distributor-role">United Kingdom &amp; Republic of Ireland</p>
                            <p className="distributor-meta">
                                Authorised distributor of MHE-manufactured MCS-NX power management and control systems
                                to the UK Emergency Services, Critical Services, and Infrastructure sectors.
                            </p>
                            <p className="distributor-meta">
                                19 Hollies Business Park, Hollies Park Road,<br />
                                Cannock, Staffordshire, WS11 1DB
                            </p>
                            <a href="https://standbygroup.com/uk/" target="_blank" rel="noopener noreferrer" className="distributor-link">
                                Visit standbygroup.com/uk →
                            </a>
                        </div>

                        <div className="distributor-card">
                            <div className="distributor-flag">🇭🇷</div>
                            <h3>Matins d.o.o.</h3>
                            <p className="distributor-role">Croatia &amp; Central / Eastern Europe</p>
                            <p className="distributor-meta">
                                Authorised distributor and integration partner supplying MHE-manufactured equipment
                                for police, fire, ambulance, and other emergency-service vehicle upfits across the region.
                            </p>
                            <p className="distributor-meta">
                                Ulica Grofova Oršić 4A,<br />
                                HR-49240 Gornja Stubica, Croatia
                            </p>
                            <a href="https://matins.hr/?lang=en" target="_blank" rel="noopener noreferrer" className="distributor-link">
                                Visit matins.hr →
                            </a>
                        </div>

                        <div className="distributor-card distributor-card-cta">
                            <div className="distributor-flag">🌍</div>
                            <h3>Become a distributor</h3>
                            <p className="distributor-role">Authorised reseller programme</p>
                            <p className="distributor-meta">
                                Interested in stocking and reselling genuine MHE-manufactured products in your region?
                                Contact us directly at the manufacturer.
                            </p>
                            <a href="mailto:sales@mhe.co.za" className="distributor-link">Contact the manufacturer →</a>
                        </div>
                    </div>
                </section>

                {/* ===== Notice for distributors ===== */}
                <section className="distributors-notice">
                    <h2 className="about-section-title">Notice to authorised distributors</h2>
                    <p>
                        When listing or linking to MCS or MCS-NX products from your own website, please link back to
                        this page or to the relevant{' '}
                        <Link to="/products" className="distributor-inline-link">MHE product page</Link> and use wording
                        that clearly identifies MHE Electronics as the manufacturer, for example:
                    </p>
                    <blockquote className="distributor-quote">
                        “The MCS and MCS-NX product range is <strong>designed, manufactured and originally sold by MHE
                        Electronics</strong>. We are an authorised distributor of MHE Electronics in our territory.”
                    </blockquote>
                    <p>
                        This helps customers and search engines correctly identify MHE Electronics as the sole owner,
                        manufacturer and original seller of the MCS and MCS-NX product range, and your company as an
                        authorised distributor.
                    </p>
                </section>
            </div>
        </div>
    );
};

export default Distributors;